// What a person reads about one machine, shared by the Machines list's three views (#6560
// Cards, #6561 List, #6562 Map): the verdict pill, the status word, the "what it is doing" line and
// where it runs. Keys only, through the caller's translator; every judgement behind them
// is the server's `verdict` (#6557).

import type { MachineHealth, MachineStatus } from '@developerz/domain';
import type { UtilBox } from './fleetUtilization';
import { shownJob } from './shown-job';

export type HealthTone = 'crit' | 'warn' | 'info';

/** The verdict pill: its words and its tone. Healthy is neutral (`info`), never green. */
export const MACHINE_HEALTH: Readonly<Record<MachineHealth, { key: string; tone: HealthTone }>> = {
  healthy: { key: 'fleet.machines.health_healthy', tone: 'info' },
  busy: { key: 'fleet.machines.health_busy', tone: 'warn' },
  overloaded: { key: 'fleet.machines.health_overloaded', tone: 'crit' },
  disk_critical: { key: 'fleet.machines.health_low_disk', tone: 'crit' },
  disk_warning: { key: 'fleet.machines.health_low_disk', tone: 'warn' },
  memory_critical: { key: 'fleet.machines.health_memory', tone: 'crit' },
  memory_warning: { key: 'fleet.machines.health_memory', tone: 'warn' },
  not_responding: { key: 'fleet.machines.health_not_responding', tone: 'crit' },
  offline: { key: 'fleet.machines.health_offline', tone: 'crit' },
  unknown: { key: 'fleet.machines.health_unknown', tone: 'info' },
};

/** The List view's Status column: one word or two per status. */
export const MACHINE_STATUS: Readonly<Record<MachineStatus, string>> = {
  working: 'fleet.machines.status_working',
  idle: 'fleet.machines.status_idle',
  paused: 'fleet.machines.status_paused',
  starting: 'fleet.machines.status_starting',
  not_responding: 'fleet.machines.status_not_responding',
  offline: 'fleet.machines.status_offline',
  waiting_to_connect: 'fleet.machines.status_waiting',
  unknown: 'fleet.machines.status_unknown',
};

/** What a machine with no task of this account's says it is doing, by status. */
const TASK_LINE: Readonly<Record<MachineStatus, string>> = {
  working: 'fleet.machines.task_working',
  idle: 'fleet.machines.task_idle',
  paused: 'fleet.machines.task_paused',
  starting: 'fleet.machines.task_starting',
  not_responding: 'fleet.machines.task_not_responding',
  offline: 'fleet.machines.task_offline',
  waiting_to_connect: 'fleet.machines.task_waiting',
  unknown: 'fleet.machines.task_unknown',
};

type Tr = (key: string, vars?: Readonly<Record<string, string | number>>) => string;

/**
 * The task line. A working machine names the job `shownJob` picks when it holds one of THIS
 * account's: its oldest RUNNING job, else its oldest job (#6745), so a paused job never
 * hides the work running behind it. The API sends no job for another account's work or a
 * machine from another org, so that reads "Working on a task", never "Waiting for work".
 */
export function taskLine(box: UtilBox, tr: Tr): string {
  const title = box.verdict.status === 'working' ? (shownJob(box)?.job.title ?? null) : null;
  return title ?? tr(TASK_LINE[box.verdict.status]);
}

/** Where it runs, as a person reads it: the server's label, else its address, else nothing. */
export const serverText = (box: UtilBox): string | null => box.server.label ?? box.server.address;
